<template>
  <div
    class="modal fade"
    :class="{ show: show, 'd-block': show }"
    tabindex="-1"
    role="dialog"
    v-if="show"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Editar Usuario</h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="userName" class="form-label">Nombre</label>
              <input
                type="text"
                class="form-control"
                id="userName"
                v-model="formData.name"
                required
                :disabled="loading"
              />
            </div>

            <div class="mb-3">
              <label for="defaultStore" class="form-label">Sucursal por Defecto</label>
              <select
                class="form-select"
                id="defaultStore"
                v-model="formData.defaultStore"
                :disabled="loading || loadingStores"
              >
                <option value="">Seleccione una sucursal</option>
                <option v-for="store in stores" :key="store.id" :value="store.division">
                  {{ store.sucursal }}
                </option>
              </select>
              <small class="form-text text-muted"
                >Seleccione la sucursal por defecto del usuario</small
              >
            </div>

            <div class="mb-3">
              <label for="userRol" class="form-label">Rol</label>
              <select
                class="form-select"
                id="userRol"
                v-model="formData.rol"
                required
                :disabled="loading || isRoleDisabled"
              >
                <option value="">Seleccione un rol</option>
                <option v-for="role in availableRoles" :key="role.value" :value="role.value">
                  {{ role.label }}
                </option>
              </select>
              <small v-if="isRoleDisabled" class="form-text text-muted">
                No puedes cambiar el rol de Administrador. Solo un Administrador puede modificar
                este rol.
              </small>
            </div>

            <div class="mb-3">
              <label for="userStatus" class="form-label">Estado</label>
              <div class="d-flex align-items-center gap-3">
                <span
                  class="badge"
                  :class="getStatusBadgeClass(formData.status)"
                  style="font-size: 0.9rem; padding: 0.5rem 1rem"
                >
                  {{ formData.status || 'N/A' }}
                </span>
                <select
                  class="form-select"
                  id="userStatus"
                  v-model="formData.status"
                  required
                  :disabled="loading"
                  style="flex: 1"
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>
              <small class="form-text text-muted"> Estado actual del usuario en el sistema </small>
            </div>

            <div v-if="error" class="alert alert-danger" role="alert">
              {{ error }}
            </div>

            <hr v-if="user" class="my-3" />

            <UserAuditLog
              v-if="user"
              :audit-log="user.auditLog"
              :created-at="user.createdAt"
              :created-by="user.createdBy"
              :updated-at="user.updatedAt"
              :updated-by="user.updatedBy"
              :stores="stores"
            />
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal" :disabled="loading">
            Cancelar
          </button>
          <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade" :class="{ show: show }" v-if="show"></div>
</template>

<script>
import { getAllStores } from '@/services/usersService'
import { mapGetters } from 'vuex'
import UserAuditLog from '@/components/users/UserAuditLog.vue'

export default {
  name: 'EditUserModal',
  emits: ['close', 'save'],
  components: {
    UserAuditLog,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        name: '',
        defaultStore: '',
        rol: '',
        status: 'activo',
      },
      loading: false,
      error: null,
      stores: [],
      loadingStores: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['userRole', 'currentUser']),
    availableRoles() {
      // Obtener el rol del usuario actual
      const currentUserRole = this.userRole || (this.currentUser && this.currentUser.rol)

      // Si el usuario actual es Administrador, puede asignar cualquier rol
      if (currentUserRole === 'Administrador') {
        return [
          { value: 'Administrador', label: 'Administrador' },
          { value: 'Soporte', label: 'Soporte' },
          { value: 'Servicio a Domicilio', label: 'Servicio a Domicilio' },
          { value: 'Transportista', label: 'Transportista' },
          { value: 'E-commerce', label: 'E-commerce' },
        ]
      }

      // Si el usuario NO es Administrador, solo puede asignar estos roles
      return [
        { value: 'Soporte', label: 'Soporte' },
        { value: 'Servicio a Domicilio', label: 'Servicio a Domicilio' },
        { value: 'Transportista', label: 'Transportista' },
        { value: 'E-commerce', label: 'E-commerce' },
      ]
    },
    isRoleDisabled() {
      // Deshabilitar el select de rol si el usuario a editar es Administrador
      // y el usuario actual NO es Administrador
      if (!this.user) return false
      const currentUserRole = this.userRole || (this.currentUser && this.currentUser.rol)
      return this.user.rol === 'Administrador' && currentUserRole !== 'Administrador'
    },
  },
  async created() {
    await this.loadStores()
  },
  watch: {
    user: {
      handler(newUser) {
        if (newUser) {
          const currentUserRole = this.userRole || (this.currentUser && this.currentUser.rol)
          let rol = newUser.rol || ''

          // Si el usuario a editar es Administrador y el usuario actual NO es Administrador,
          // no permitir cambiar el rol (mantener el valor pero no permitir modificarlo)
          if (rol === 'Administrador' && currentUserRole !== 'Administrador') {
            // Mantener el rol Administrador pero no permitir cambiarlo
            rol = 'Administrador'
          }

          // Si el rol no está en los roles disponibles, usar el primer rol disponible
          const availableRoleValues = this.availableRoles.map(r => r.value)
          if (rol && !availableRoleValues.includes(rol)) {
            rol = availableRoleValues[0] || ''
          }

          this.formData = {
            name: newUser.name || '',
            defaultStore: newUser.defaultStore || '',
            rol: rol,
            status: newUser.status || 'activo',
          }
          this.error = null
        }
      },
      immediate: true,
    },
    show(newVal) {
      if (newVal && this.user) {
        const currentUserRole = this.userRole || (this.currentUser && this.currentUser.rol)
        let rol = this.user.rol || ''

        // Si el usuario a editar es Administrador y el usuario actual NO es Administrador,
        // no permitir cambiar el rol
        if (rol === 'Administrador' && currentUserRole !== 'Administrador') {
          rol = 'Administrador'
        }

        // Si el rol no está en los roles disponibles, usar el primer rol disponible
        const availableRoleValues = this.availableRoles.map(r => r.value)
        if (rol && !availableRoleValues.includes(rol)) {
          rol = availableRoleValues[0] || ''
        }

        this.formData = {
          name: this.user.name || '',
          defaultStore: this.user.defaultStore || '',
          rol: rol,
          status: this.user.status || 'activo',
        }
        this.error = null
      }
    },
    availableRoles() {
      // Si los roles disponibles cambian y el rol actual no está disponible,
      // ajustar el valor
      if (this.formData.rol) {
        const availableRoleValues = this.availableRoles.map(r => r.value)
        if (!availableRoleValues.includes(this.formData.rol)) {
          // Si el usuario actual no es Administrador y el rol es Administrador,
          // mantenerlo pero será deshabilitado
          const currentUserRole = this.userRole || (this.currentUser && this.currentUser.rol)
          if (this.formData.rol === 'Administrador' && currentUserRole !== 'Administrador') {
            // Mantener Administrador pero será de solo lectura
            return
          }
          // En otros casos, usar el primer rol disponible
          this.formData.rol = availableRoleValues[0] || ''
        }
      }
    },
  },
  methods: {
    getStatusBadgeClass(status) {
      if (!status) return 'badge-secondary'

      const statusLower = String(status).toLowerCase()
      if (statusLower === 'activo') {
        return 'badge-success'
      } else if (statusLower === 'inactivo') {
        return 'badge-danger'
      } else {
        return 'badge-warning'
      }
    },
    async loadStores() {
      this.loadingStores = true
      try {
        const result = await getAllStores()
        if (result.success) {
          this.stores = result.data || []
        } else {
          console.error('Error loading stores:', result.error)
          this.stores = []
        }
      } catch (error) {
        console.error('Error loading stores:', error)
        this.stores = []
      } finally {
        this.loadingStores = false
      }
    },
    closeModal() {
      this.$emit('close')
      this.error = null
    },
    async handleSubmit() {
      if (!this.user) return

      this.loading = true
      this.error = null

      try {
        await this.$emit('save', {
          uid: this.user.uid,
          ...this.formData,
        })
        this.closeModal()
      } catch (error) {
        this.error = error.message || 'Error al guardar cambios'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modal {
  &.show {
    opacity: 1;
  }
}

.modal-backdrop {
  &.show {
    opacity: 0.5;
  }
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.badge {
  padding: 0.35em 0.65em;
  font-size: 0.875em;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}
</style>
